'use client';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';

import { CrudContext } from '../shared/context';
import { useActivePage } from '../useActivePage';
import { useNotifications } from '../useNotifications';
import { useLocaleText } from '../AppProvider/LocalizationProvider';
import { PageContainer, type PageContainerProps } from '../PageContainer';

import { DataSourceCache } from './cache';
import { useFormValidation } from './useFormValidation';
import { useCachedDataSource } from './useCachedDataSource';
import { CrudForm, CrudFormSlots, CrudFormSlotProps } from './CrudForm';
import { OmitId, DataModel, DataSource, DataFieldFormValue } from './types';
import { type CRUDLocaleText, CRUD_DEFAULT_LOCALE_TEXT } from './localeText';

export interface CreateProps<D extends DataModel> {
  /**
   * The title of the page.
   */
  pageTitle?: string;
  /**
   * Whether the form fields should reset after the form is submitted.
   * @default false
   */
  resetOnSubmit?: boolean;
  /**
   * Locale text for the component.
   */
  localeText?: CRUDLocaleText;
  /**
   * Initial form values.
   * @default {}
   */
  initialValues?: Partial<OmitId<D>>;
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache?: DataSourceCache | null;
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource?: DataSource<D> & Required<Pick<DataSource<D>, 'createOne'>>;
  /**
   * Callback fired when the form is successfully submitted.
   */
  onSubmitSuccess?: (formValues: Partial<OmitId<D>>) => Promise<void> | void;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    form?: CrudFormSlotProps;
    pageContainer?: PageContainerProps;
  };
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: {
    form?: CrudFormSlots;
    pageContainer?: React.JSXElementConstructor<PageContainerProps>;
  };
}

/**
 *
 * Demos:
 *
 * - [CRUD](https://mui.com/toolpad/core/react-crud/)
 *
 * API:
 *
 * - [Create API](https://mui.com/toolpad/core/api/create)
 */
function Create<D extends DataModel>(props: CreateProps<D>) {
  const {
    slots,
    pageTitle,
    slotProps,
    onSubmitSuccess,
    dataSourceCache,
    resetOnSubmit = false,
    localeText: propsLocaleText,
    initialValues = {} as Partial<OmitId<D>>,
  } = props;

  const globalLocaleText = useLocaleText();
  const localeText = {
    ...CRUD_DEFAULT_LOCALE_TEXT,
    ...globalLocaleText,
    ...propsLocaleText,
  };

  const crudContext = React.useContext(CrudContext);
  const dataSource = (props.dataSource ??
    crudContext.dataSource) as NonNullable<typeof props.dataSource>;

  const notifications = useNotifications();

  invariant(dataSource, 'No data source found.');

  const cache = React.useMemo(() => {
    const manualCache = dataSourceCache ?? crudContext.dataSourceCache;
    return typeof manualCache !== 'undefined'
      ? manualCache
      : new DataSourceCache();
  }, [crudContext.dataSourceCache, dataSourceCache]);
  const cachedDataSource = useCachedDataSource<D>(
    dataSource,
    cache
  ) as NonNullable<typeof props.dataSource>;

  const { fields, validate, createOne } = cachedDataSource;

  const activePage = useActivePage();

  const [formState, setFormState] = React.useState<{
    values: Partial<OmitId<D>>;
    errors: Partial<Record<keyof D, string>>;
  }>(() => ({
    errors: {},
    values: {
      ...Object.fromEntries(
        fields
          .filter(({ field, editable }) => field !== 'id' && editable !== false)
          .map(({ type, field }) => [
            field,
            type === 'boolean'
              ? (initialValues[field] ?? false)
              : initialValues[field],
          ])
      ),
      ...initialValues,
    },
  }));
  const formValues = formState.values;
  const { validateForm, validateField, resetValidation } = useFormValidation<D>(
    { fields, validate },
    setFormState
  );

  const setFormValues = React.useCallback(
    (newFormValues: Partial<OmitId<D>>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    []
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<Record<keyof D, string>>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    []
  );

  const handleFormFieldChange = React.useCallback(
    (
      name: keyof D,
      value: DataFieldFormValue,
      updates?: Record<string, DataFieldFormValue>
    ) => {
      const newFormValues = { ...formValues, ...updates, [name]: value };

      setFormValues(newFormValues);
      void validateField(name, newFormValues);
    },
    [formValues, setFormValues, validateField]
  );

  const handleFormReset = React.useCallback(() => {
    resetValidation();
    setFormValues(initialValues);
    setFormErrors({});
  }, [initialValues, setFormValues, setFormErrors, resetValidation]);

  const handleFormSubmit = React.useCallback(async () => {
    if (!(await validateForm(formValues))) return;

    try {
      await createOne(formValues);
      notifications.show(localeText.createSuccessMessage, {
        severity: 'success',
        autoHideDuration: 3000,
      });

      if (onSubmitSuccess) {
        await onSubmitSuccess(formValues);
      }

      if (resetOnSubmit) {
        handleFormReset();
      }
    } catch (createError) {
      notifications.show(
        `${localeText.createErrorMessage} ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        }
      );
    }
  }, [
    createOne,
    formValues,
    handleFormReset,
    localeText.createErrorMessage,
    localeText.createSuccessMessage,
    notifications,
    onSubmitSuccess,
    resetOnSubmit,
    validateForm,
  ]);

  const PageContainerSlot = slots?.pageContainer ?? PageContainer;

  return (
    <PageContainerSlot
      title={pageTitle}
      breadcrumbs={
        activePage && pageTitle
          ? [
              ...activePage.breadcrumbs,
              {
                title: pageTitle,
              },
            ]
          : undefined
      }
      {...slotProps?.pageContainer}
    >
      <CrudForm
        slots={slots?.form}
        formState={formState}
        dataSource={dataSource}
        onReset={handleFormReset}
        onSubmit={handleFormSubmit}
        slotProps={slotProps?.form}
        onFieldChange={handleFormFieldChange}
        submitButtonLabel={localeText.createLabel}
      />
    </PageContainerSlot>
  );
}

Create.propTypes /* remove-proptypes */ = {
  /**
   * The title of the page.
   */
  pageTitle: PropTypes.string,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: PropTypes.object,
  /**
   * Locale text for the component.
   */
  localeText: PropTypes.object,
  /**
   * Whether the form fields should reset after the form is submitted.
   * @default false
   */
  resetOnSubmit: PropTypes.bool,
  /**
   * Initial form values.
   * @default {}
   */
  initialValues: PropTypes.object,
  /**
   * Callback fired when the form is successfully submitted.
   */
  onSubmitSuccess: PropTypes.func,
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache: PropTypes.shape({
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    ttl: PropTypes.number.isRequired,
    cache: PropTypes.object.isRequired,
  }),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    pageContainer: PropTypes.object,
    form: PropTypes.shape({
      select: PropTypes.object,
      checkbox: PropTypes.object,
      textField: PropTypes.object,
      datePicker: PropTypes.object,
      dateTimePicker: PropTypes.object,
    }),
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    pageContainer: PropTypes.elementType,
    form: PropTypes.shape({
      select: PropTypes.elementType,
      checkbox: PropTypes.elementType,
      textField: PropTypes.elementType,
      datePicker: PropTypes.elementType,
      dateTimePicker: PropTypes.elementType,
    }),
  }),
} as any;

export { Create };
